// 色の相互変換 (RGB / HSV / HEX)。値域: RGB 0..255、H 0..360、S/V 0..100。

export interface RGB {
  r: number
  g: number
  b: number
}
export interface HSV {
  h: number
  s: number
  v: number
}

export function clampByte(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)))
}

export function rgbToHex({ r, g, b }: RGB): string {
  return '#' + [r, g, b].map(v => clampByte(v).toString(16).padStart(2, '0')).join('')
}

export function hexToRgb(hex: string): RGB | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  let h = m[1]!
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  const n = parseInt(h, 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

export function rgbToHsv({ r, g, b }: RGB): HSV {
  const rr = r / 255, gg = g / 255, bb = b / 255
  const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb)
  const d = max - min
  let h = 0
  if (d > 0) {
    if (max === rr) h = ((gg - bb) / d) % 6
    else if (max === gg) h = (bb - rr) / d + 2
    else h = (rr - gg) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  return { h, s: max === 0 ? 0 : (d / max) * 100, v: max * 100 }
}

export function hsvToRgb({ h, s, v }: HSV): RGB {
  const ss = Math.max(0, Math.min(100, s)) / 100
  const vv = Math.max(0, Math.min(100, v)) / 100
  const hh = (((h % 360) + 360) % 360) / 60
  const c = vv * ss
  const x = c * (1 - Math.abs((hh % 2) - 1))
  const m = vv - c
  let r = 0, g = 0, b = 0
  if (hh < 1) [r, g, b] = [c, x, 0]
  else if (hh < 2) [r, g, b] = [x, c, 0]
  else if (hh < 3) [r, g, b] = [0, c, x]
  else if (hh < 4) [r, g, b] = [0, x, c]
  else if (hh < 5) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  return { r: clampByte((r + m) * 255), g: clampByte((g + m) * 255), b: clampByte((b + m) * 255) }
}

/** 0..1 の正規化 RGB */
export function rgbToUnit({ r, g, b }: RGB): [number, number, number] {
  return [r / 255, g / 255, b / 255]
}
